import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../services/api';

const PhotoBoothPanel = () => {
  const { slug, token } = useParams();
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timeoutRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploaderName, setUploaderName] = useState('');
  const [message, setMessage] = useState('');
  
  // Video states
  const [mode, setMode] = useState('photo'); // 'photo' | 'video'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [hasCamera, setHasCamera] = useState(null); // null = checking, true/false

  const MAX_RECORDING_TIME = 15; // seconds

  useEffect(() => {
    checkCameraAvailability();
    loadProjectInfo();
    
    return () => {
      isMountedRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [slug]);

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().catch(() => {});
      };
    }
  }, [showCamera, stream]);

  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setHasCamera(false);
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(device => device.kind === 'videoinput');
      setHasCamera(hasVideoInput);
    } catch (err) {
      setHasCamera(false);
    }
  };

  const loadProjectInfo = async () => {
    try {
      const response = token 
        ? await publicApi.get(`/photobooth/${slug}/panel/${token}`)
        : await publicApi.get(`/photobooth/${slug}/info`);
      
      if (!response.data.success) {
        setError(response.data.error || 'Proyecto no encontrado');
        setLoading(false);
        return;
      }
      
      setProjectInfo(response.data.info);
      
      if (response.data.info.branding) {
        const branding = response.data.info.branding;
        document.documentElement.style.setProperty('--primary-color', branding.primaryColor || '#3B82F6');
        document.documentElement.style.setProperty('--secondary-color', branding.secondaryColor || '#10B981');
      }
    } catch (err) {
      setError('Error al cargar el proyecto');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async (forVideo = false) => {
    const isSecure = window.location.protocol === 'https:' || 
                     window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1';
    
    if (!isSecure) {
      setError('La cámara requiere conexión segura (HTTPS). Usa https:// en la URL.');
      return;
    }
    
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Tu navegador no soporta acceso a la cámara.');
      return;
    }
    
    try {
      const constraints = {
        video: { 
          facingMode: 'user', 
          width: { ideal: 1280, min: 640 }, 
          height: { ideal: 720, min: 480 } 
        },
        audio: forVideo // Include audio for video recording
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setShowCamera(true);
      setPreviewImage(null);
      setCapturedPhoto(null);
      setPreviewVideo(null);
      setRecordedVideo(null);
      setError(null);
    } catch (err) {
      let errorMessage = 'No se pudo acceder a la cámara';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Permiso de cámara denegado. Permite el acceso en la configuración del navegador.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No se encontró cámara en el dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo usada por otra aplicación.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'La cámara no soporta la resolución solicitada.';
      }
      
      setShowCamera(false);
      setError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (isRecording) {
      stopRecording();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    setShowCamera(false);
    setCapturedPhoto(null);
    setPreviewImage(null);
  };

  // Photo capture
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas || !video.videoWidth) {
      setError('La cámara aún no está lista.');
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }
        setCapturedPhoto(blob);
        setPreviewImage(URL.createObjectURL(blob));
        setShowCamera(false);
      } else {
        setError('Error al procesar la imagen');
      }
    }, 'image/jpeg', 0.85);
  };

  // Video recording
  const startRecording = () => {
    if (!stream) return;
    
    recordedChunksRef.current = [];
    
    // Try different MIME types for better compatibility
    let mimeType = 'video/mp4';
    if (!MediaRecorder.isTypeSupported('video/mp4')) {
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        mimeType = 'video/webm;codecs=vp9';
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        mimeType = 'video/webm';
      }
    }
    
    try {
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps for HD
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        setRecordedVideo(blob);
        setPreviewVideo(URL.createObjectURL(blob));
        generateThumbnail(blob);
        
        // Stop camera
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }
        setShowCamera(false);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      setError('Error al iniciar la grabación: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    setIsRecording(false);
  };

  const generateThumbnail = (videoBlob) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(videoBlob);
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    
    video.onloadeddata = () => {
      video.currentTime = 0.1; // Seek to slightly after start
    };
    
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          setVideoThumbnail(URL.createObjectURL(blob));
        }
      }, 'image/jpeg', 0.8);
      
      URL.revokeObjectURL(url);
    };
  };

  const retakePhoto = () => {
    if (previewImage) URL.revokeObjectURL(previewImage);
    if (previewVideo) URL.revokeObjectURL(previewVideo);
    setCapturedPhoto(null);
    setPreviewImage(null);
    setRecordedVideo(null);
    setPreviewVideo(null);
    setVideoThumbnail(null);
  };

  const processImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxSize = 1920;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      
      img.src = url;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setPreviewImage(URL.createObjectURL(file));
    setCapturedPhoto(file);
    setShowCamera(false);
    setMode('photo');
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > MAX_RECORDING_TIME + 1) {
        setError(`El video debe durar máximo ${MAX_RECORDING_TIME} segundos`);
        return;
      }
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      
      setRecordedVideo(file);
      setPreviewVideo(URL.createObjectURL(file));
      generateThumbnail(file);
      setShowCamera(false);
      setMode('video');
    };
    video.src = URL.createObjectURL(file);
  };

  const uploadPhoto = async (file) => {
    if (!file || !uploaderName.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    
    setUploading(true);
    setUploadProgress('Procesando imagen...');
    setUploadSuccess(false);
    setError(null);
    
    try {
      setUploadProgress('Optimizando imagen...');
      const processedBlob = await processImage(file);
      
      setUploadProgress('Subiendo imagen...');
      const formData = new FormData();
      formData.append('photo', processedBlob, 'photo.jpg');
      formData.append('uploaderName', uploaderName.trim());
      formData.append('message', message.trim());
      
      const response = await publicApi.post(`/photobooth/${slug}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        setUploadSuccess(true);
        setCapturedPhoto(null);
        setUploaderName('');
        setMessage('');
        setUploadProgress('');
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setUploadSuccess(false);
            setPreviewImage(null);
          }
        }, 3000);
      } else {
        setError(response.data.error || 'Error al subir la foto');
        setUploadProgress('');
      }
    } catch {
      setError('Error al subir la foto');
      setUploadProgress('');
    } finally {
      setUploading(false);
      if (streamRef.current) stopCamera();
    }
  };

  const uploadVideo = async () => {
    if (!recordedVideo || !uploaderName.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    
    setUploading(true);
    setUploadProgress('Procesando video...');
    setUploadSuccess(false);
    setError(null);
    
    try {
      // Get video duration
      const video = document.createElement('video');
      video.preload = 'metadata';
      const duration = await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          resolve(0); // Default duration if can't be determined
        };
        video.src = URL.createObjectURL(recordedVideo);
      });
      
      setUploadProgress('Subiendo video...');
      const formData = new FormData();
      formData.append('video', recordedVideo, 'video.mp4');
      formData.append('uploaderName', uploaderName.trim());
      formData.append('message', message.trim());
      formData.append('durationSeconds', duration.toString());
      
      // Add thumbnail if available - wait for it to complete
      if (videoThumbnail) {
        try {
          const thumbResponse = await fetch(videoThumbnail);
          const thumbnailBlob = await thumbResponse.blob();
          const thumbnailDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(thumbnailBlob);
          });
          if (thumbnailDataUrl) {
            formData.append('thumbnail', thumbnailDataUrl);
          }
        } catch (thumbErr) {
          console.warn('Error processing thumbnail:', thumbErr);
          // Continue without thumbnail
        }
      }
      
      const response = await publicApi.post(`/photobooth/${slug}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        setUploadSuccess(true);
        setRecordedVideo(null);
        setPreviewVideo(null);
        setVideoThumbnail(null);
        setUploaderName('');
        setMessage('');
        setUploadProgress('');
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setUploadSuccess(false);
          }
        }, 3000);
      } else {
        setError(response.data.error || 'Error al subir el video');
        setUploadProgress('');
      }
    } catch (err) {
      console.error('Error uploading video:', err);
      setError('Error al subir el video');
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error && !projectInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-sm w-full">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 flex flex-col">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto text-center">
          <h1 
            className="text-xl sm:text-2xl font-bold truncate"
            style={{ color: projectInfo?.branding?.primaryColor || '#3B82F6' }}
          >
            {projectInfo?.projectName || 'Panel Fotográfico'}
          </h1>
        </div>
      </div>

      <div className="flex-1 flex flex-col max-w-lg mx-auto w-full p-4">
        {uploadSuccess && (
          <div className="mb-4 p-4 bg-green-500 text-white rounded-xl flex items-center justify-center shadow-lg animate-pulse">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">
              {mode === 'video' ? '¡Video enviado exitosamente!' : '¡Foto enviada exitosamente!'}
            </span>
          </div>
        )}

        {/* Preview de foto o video */}
        {(previewImage || previewVideo) && !uploadSuccess && (
          <div className="mb-4 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="relative">
              {mode === 'video' && previewVideo ? (
                <video 
                  src={previewVideo} 
                  controls 
                  className="w-full h-auto max-h-80 object-contain bg-black"
                />
              ) : (
                <img src={previewImage} alt="Vista previa" className="w-full h-auto max-h-80 object-contain bg-black" />
              )}
              
              {uploading && (
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-4"></div>
                  <p className="text-white font-medium text-center px-4">{uploadProgress}</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <div className="space-y-3 mb-4">
                <div>
                  <input
                    type="text"
                    placeholder="Tu nombre *"
                    value={uploaderName}
                    onChange={(e) => setUploaderName(e.target.value.slice(0, 50))}
                    maxLength={50}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{uploaderName.length}/50</p>
                </div>
                <div>
                  <textarea
                    placeholder="Mensaje para la presentación (opcional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 120))}
                    maxLength={120}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{message.length}/120</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    retakePhoto();
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    if (videoFileInputRef.current) videoFileInputRef.current.value = '';
                  }}
                  disabled={uploading}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition disabled:opacity-50 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Cambiar
                </button>
                <button
                  onClick={() => mode === 'video' ? uploadVideo() : uploadPhoto(capturedPhoto)}
                  disabled={uploading || !uploaderName.trim()}
                  className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition disabled:opacity-50 disabled:bg-gray-400 flex items-center justify-center"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl flex items-center">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-700 hover:text-red-900">×</button>
          </div>
        )}

        {!previewImage && !previewVideo && !uploadSuccess && (
          <div className="bg-white rounded-2xl shadow-lg p-6 flex-1 flex flex-col">
            {showCamera ? (
              <div className="flex-1 flex flex-col">
                <div className="relative bg-black rounded-xl overflow-hidden flex-1 flex items-center justify-center min-h-64">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {isRecording && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-3 py-1 rounded-full flex items-center">
                      <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                      REC {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
                    </div>
                  )}
                  
                  {!isRecording && stream?.getVideoTracks().length > 0 && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                      Cámara activa
                    </div>
                  )}
                  
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    {mode === 'photo' ? (
                      <button
                        onClick={capturePhoto}
                        className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                      >
                        <div className="w-14 h-14 bg-green-500 rounded-full"></div>
                      </button>
                    ) : (
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform ${
                          isRecording ? 'bg-red-600' : 'bg-white'
                        }`}
                      >
                        {isRecording ? (
                          <div className="w-6 h-6 bg-white rounded-sm"></div>
                        ) : (
                          <div className="w-14 h-14 bg-red-600 rounded-full"></div>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 flex gap-2">
                  <button onClick={stopCamera} className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition">
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center">
                {/* Selector de modo - solo si hay cámara */}
                {hasCamera && (
                  <div className="flex justify-center mb-6">
                    <div className="bg-gray-100 rounded-xl p-1 flex">
                      <button
                        onClick={() => setMode('photo')}
                        className={`px-6 py-2 rounded-lg font-medium transition ${
                          mode === 'photo' 
                            ? 'bg-white shadow text-blue-600' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        📷 Foto
                      </button>
                      <button
                        onClick={() => setMode('video')}
                        className={`px-6 py-2 rounded-lg font-medium transition ${
                          mode === 'video' 
                            ? 'bg-white shadow text-blue-600' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        🎬 Video
                      </button>
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {mode === 'video' ? (
                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </div>
                  <h2 className="text-lg font-bold text-gray-800 mb-2">
                    {mode === 'video' ? 'Graba tu video' : 'Sube tu foto'}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    {mode === 'video' 
                      ? `Graba un video de hasta ${MAX_RECORDING_TIME} segundos`
                      : 'Toma una foto o selecciona de tu galería'
                    }
                  </p>
                </div>
                
                <div className="space-y-3">
                  {/* Botones de cámara - solo si hay cámara */}
                  {hasCamera && (
                    <button
                      onClick={() => startCamera(mode === 'video')}
                      className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition flex items-center justify-center"
                    >
                      {mode === 'video' ? (
                        <>
                          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Grabar Video
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Tomar Foto
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Inputs de archivo */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <input
                    type="file"
                    ref={videoFileInputRef}
                    onChange={handleVideoFileSelect}
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                  />
                  
                  {/* Botones de galería */}
                  {hasCamera ? (
                    // Si hay cámara, mostrar botón según modo
                    <button
                      onClick={() => mode === 'video' ? videoFileInputRef.current?.click() : fileInputRef.current?.click()}
                      className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition flex items-center justify-center"
                    >
                      {mode === 'video' ? (
                        <>
                          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Seleccionar video de galería
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Seleccionar de tu galería
                        </>
                      )}
                    </button>
                  ) : (
                    // Si no hay cámara, mostrar ambos botones de selección
                    <>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition flex items-center justify-center"
                      >
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Seleccionar Foto
                      </button>
                      <button
                        onClick={() => videoFileInputRef.current?.click()}
                        className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition flex items-center justify-center"
                      >
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Seleccionar Video
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {!previewImage && !previewVideo && !uploadSuccess && (
          <div className="mt-4 bg-white rounded-xl shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">¿Cómo funciona?</h3>
            <ol className="text-xs text-gray-600 space-y-1">
              <li className="flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                <span>{hasCamera ? 'Graba o selecciona una foto/video' : 'Selecciona una foto o video'}</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                <span>Ingresa tu nombre (obligatorio)</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                <span>Envía al evento</span>
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoBoothPanel;
